import React, { useState, useRef, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { PlusCircle, Upload, X, Pill, CreditCard } from 'lucide-react';
import ManagementPage from '../../Common/components/common/ManagementPage';
import DetailItem from '../../Common/components/common/DetailItem';
import DetailGrid from '../../Common/components/common/DetailGrid';
import ModalInput from '../../Common/components/common/ModalInput';
import { useToast } from '../../Common/context/ToastContext';
import { getCpCustomerList } from '../api/cp';

/** API 고객(약국) 목록 응답을 테이블용 배열로 정규화 (data.rows, 담당자·대표자명 포함) */
function normalizeCustomerList(data) {
  const rows = data?.rows ?? data?.list ?? (Array.isArray(data) ? data : []);
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => ({
    id: row.id ?? row.pharmacy_id ?? row.pharmacyId ?? row.customer_id,
    cp_id: row.cp_id ?? row.cpId,
    terminal_id: row.terminal_id ?? row.terminalId,
    pharmacy_id: row.pharmacy_id ?? row.pharmacyId,
    status: row.status ?? 'ACTIVE',
    created_at: row.created_at ?? row.createdAt ?? '',
    updated_at: row.updated_at ?? row.updatedAt ?? '',
    institution_code: row.institution_code ?? row.institutionCode ?? '',
    name: row.name ?? row.pharmacyName ?? row.pharmacy_name ?? '',
    phone: row.phone ?? row.tel ?? '',
    address: row.address ?? row.addr ?? row.location ?? '',
    business_no: row.business_no ?? row.businessNo ?? '',
    driver: row.driver ?? row.driver_name ?? row.driverName ?? '',
    representative: row.representative ?? row.boss_name ?? row.bossName ?? row.owner ?? row.representative_name ?? '',
  }));
}

/** 엑셀 행 → 약국 객체 (컬럼명: 약국명, 주소/위치, 담당자, 연락처, 전월매출 등) */
function rowToPharmacy(row, index) {
  const name = row['약국명'] ?? row['A'] ?? row[0] ?? '';
  const location = row['주소'] ?? row['위치'] ?? row['B'] ?? row[1] ?? '';
  const owner = row['담당자'] ?? row['담당자명'] ?? row['C'] ?? row[2] ?? '';
  const contact = row['연락처'] ?? row['D'] ?? row[3] ?? '';
  const lastMonthSales = row['전월매출'] ?? row['E'] ?? row[4] ?? '';
  if (!name && !location && !owner && !contact) return null;
  return {
    id: `P-upload-${Date.now()}-${index}`,
    name: String(name).trim() || '-',
    location: String(location).trim() || '-',
    owner: String(owner).trim() || '-',
    contact: String(contact).trim() || '-',
    status: '연동중',
    lastMonthSales: String(lastMonthSales).trim() ? String(lastMonthSales).trim() : '-',
  };
}

function formatSales(val) {
  if (val == null || val === '' || val === '-') return '-';
  const n = typeof val === 'number' ? val : Number(String(val).replace(/[^0-9.-]/g, ''));
  if (Number.isNaN(n)) return val;
  return n.toLocaleString('ko-KR') + '원';
}

function PharmacyManage() {
  const { showToast } = useToast();
  const [pharmacies, setPharmacies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isNewPharmacyModalOpen, setIsNewPharmacyModalOpen] = useState(false);
  const fileInputRef = useRef(null);

  const fetchList = async (params = {}) => {
    try {
      setLoading(true);
      const data = await getCpCustomerList(params);
      setPharmacies(normalizeCustomerList(data));
    } catch (e) {
      showToast(e.message || '약국 목록을 불러오지 못했습니다.');
      setPharmacies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  const handlePayment = (item) => {
    if (!item) return;
    // 수기결제(sugi) 페이지에서 사용하는 약국 정보 저장
    const code = item.institution_code || item.pharmacy_id || item.id || '';
    localStorage.setItem('currentSelectedPharmacyForPayment', JSON.stringify({
      name: item.name || '',
      code,
    }));
    const url = `${window.location.origin}/cp/sugi`;
    window.open(url, 'sugiPopup', 'noopener,noreferrer,width=450,height=750,scrollbars=yes,resizable=yes');
  };

  const handleExcelUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = ev.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const firstSheet = workbook.SheetNames[0];
        const sheet = workbook.Sheets[firstSheet];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
        const added = rows.map((row, i) => rowToPharmacy(row, i)).filter(Boolean);
        if (added.length === 0) {
          showToast('엑셀에서 유효한 약국 데이터를 찾을 수 없습니다.');
          return;
        }
        setPharmacies((prev) => [...prev, ...added]);
        showToast(`엑셀에서 ${added.length}건이 추가되었습니다. (목록은 API 기준으로 유지됩니다.)`);
      } catch (err) {
        showToast('엑셀 파일을 읽는 중 오류가 발생했습니다.');
      }
      e.target.value = '';
    };
    reader.readAsBinaryString(file);
  };

  return (
    <div className="relative flex-1 flex flex-col min-h-0 overflow-hidden">
    <input
      ref={fileInputRef}
      type="file"
      accept=".xlsx,.xls"
      className="hidden"
      onChange={handleExcelUpload}
    />
    <ManagementPage
      title="약국관리"
      emptyStateIcon={<Pill size={32} className="opacity-20" />}
      onExcelDownload={() => showToast('엑셀 다운로드가 시작되었습니다.')}
      // leftActions={
      //   <button
      //     type="button"
      //     onClick={() => selectedItem && handlePayment(selectedItem)}
      //     disabled={!selectedItem}
      //     className="inline-flex items-center gap-2 px-3 py-1.5 bg-[var(--brand-primary-hover)] text-white rounded-lg font-bold text-[14px] hover:bg-[var(--brand-primary-active)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      //   >
      //     <CreditCard size={14} /> 결제
      //   </button>
      // }
      extraButtons={
        <>
          {/* <button
            onClick={() => setIsNewPharmacyModalOpen(true)}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-[var(--brand-primary)] text-white rounded-lg font-bold text-[14px] hover:bg-[var(--brand-primary-hover)] transition-colors"
          >
            <PlusCircle size={14} /> 신규등록
          </button>
          <button
            type="button"
            onClick={() => showToast('준비중입니다.')}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#191f28] text-white rounded-lg font-bold text-[14px] hover:bg-black transition-colors"
          >
            <Upload size={14} /> 엑셀업로드
          </button> */}
        </>
      }
      excelButtonClassName="inline-flex items-center gap-2 px-3 py-1.5 bg-[#191f28] text-white rounded-lg font-bold text-[14px] hover:bg-black transition-colors"
      tableHeaders={
        <>
          <th className="px-4 py-3">약국명</th>
          <th className="px-4 py-3">담당자</th>
          <th className="px-4 py-3">대표자명</th>
          <th className="px-4 py-3">연락처</th>
        </>
      }
      data={pharmacies}
      renderRow={(item) => (
        <>
          <td className="px-4 py-3 font-bold text-[#191f28]">{item.name || '-'}</td>
          <td className="px-4 py-3 font-medium text-[#4e5968]">{item.driver || '-'}</td>
          <td className="px-4 py-3 font-medium text-[#4e5968]">{item.representative || '-'}</td>
          <td className="px-4 py-3 font-mono text-[#4e5968]">{item.phone || '-'}</td>
        </>
      )}
      detailTitle="약국 상세정보"
      detailButtonLabel="결제"
      detailButtonIcon={<CreditCard size={14} />}
      detailButtonSmall
      detailButtonDisabled={!selectedItem}
      onDetailButtonClick={() => handlePayment(selectedItem)}
      selectedItem={selectedItem}
      onSelectItem={setSelectedItem}
      renderDetail={(item) => (
        <>
          <DetailGrid>
            <DetailItem label="약국 ID" value={item.pharmacy_id ?? '-'} />
            <DetailItem label="대표자명" value={item.representative || '-'} />
            <DetailItem label="약국명" value={item.name || '-'} />
            <DetailItem label="약국코드" value={item.institution_code || '-'} />
            <DetailItem label="사업자등록번호" value={item.business_no || '-'} />
            <DetailItem label="연락처" value={item.phone || '-'} />
            <DetailItem label="담당자" value={item.driver || '-'} />
            <DetailItem label="등록일" value={item.created_at || '-'} />
          </DetailGrid>
          <div className="flex justify-between items-center gap-4 min-w-0 mt-[1.5rem]">
            <span className="text-[13px] font-bold text-[#8b95a1] shrink-0">주소</span>
            <span className="text-[16px] font-bold text-[#191f28] text-right whitespace-nowrap overflow-hidden text-ellipsis min-w-0 flex-1" title={item.address || ''}>
              {item.address || '-'}
            </span>
          </div>
        </>
      )}
    />

      {/* 신규 약국 등록 모달 */}
      {isNewPharmacyModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white w-[520px] rounded shadow-2xl overflow-hidden flex flex-col border border-[#e5e8eb]">
            <div className="px-8 py-6 flex items-center justify-between border-b border-[#f2f4f6]">
              <h2 className="text-[24px] font-black text-[#191f28]">신규 약국 등록</h2>
              <button onClick={() => setIsNewPharmacyModalOpen(false)} className="p-2 text-[#adb5bd] hover:text-[#191f28]">
                <X size={24} />
              </button>
            </div>
            <div className="py-5 px-8 space-y-6 overflow-y-auto max-h-[500px] custom-scrollbar">
              <div className="p-4 bg-[var(--brand-primary-soft)] rounded-2xl flex items-center gap-3 border border-[var(--brand-primary-border)]">
                <div className="p-2 bg-white rounded-lg"><Pill size={16} className="text-[var(--brand-primary)]" /></div>
                <span className="text-[13px] font-bold text-[var(--brand-primary)]">약국 정보를 등록합니다.</span>
              </div>
              <ModalInput label="약국명" placeholder="예: 푸른온누리약국" />
              <ModalInput label="주소" placeholder="예: 서울 강남구 테헤란로 123" />
              <ModalInput label="담당자명" placeholder="성함을 입력하세요" />
              <ModalInput label="연락처" placeholder="02-123-4567 또는 010-0000-0000" />
            </div>
            <div className="px-8 py-6 bg-[#f9fafb] border-t border-[#f2f4f6] flex gap-3">
              <button onClick={() => setIsNewPharmacyModalOpen(false)} className="flex-1 py-4 bg-[#e5e8eb] text-[#4e5968] rounded-2xl font-bold text-[16px]">
                취소
              </button>
              <button
                onClick={() => {
                  setIsNewPharmacyModalOpen(false);
                  showToast('신규 약국이 등록되었습니다.');
                }}
                className="flex-1 py-4 bg-[var(--brand-primary)] text-white rounded-2xl font-bold text-[16px] shadow-lg shadow-[var(--brand-primary)]/20"
              >
                등록
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PharmacyManage;
